/**
 * <CardDeck /> — universal tap-to-reveal absorption card.
 *
 * Hybrid spec (Reclaim calm + Apple clarity + gentle visual learning):
 *
 *   ┌──────────────────────────────┐
 *   │     Soft Illustration        │  ← top 40-50% (CardIllustration)
 *   │       (flat, calm)           │
 *   ├──────────────────────────────┤
 *   │   Question (Playfair italic) │  ← serif for emotion
 *   │                              │
 *   │      Tap to reveal           │  ← faint hint
 *   ├──── (tap) ───────────────────┤
 *   │   Answer (Inter, clean)      │  ← sans-serif for clarity
 *   │   [ Got it → ]               │  ← warm gold, soft
 *   └──────────────────────────────┘
 *
 * No score, no streaks, no failure tone. Recognition over recall.
 */
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";
import CardIllustration from "./CardIllustration";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const VARIANT_COPY = {
  flash:     { label: "Flash",     hint: "Tap to reveal" },
  precision: { label: "Precision", hint: "Tap to reveal" },
  scenario:  { label: "Scenario",  hint: "Tap to consider" },
};

export default function CardDeck({ variant = "flash", cards = [], onComplete }) {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [softQueue, setSoftQueue] = useState([]);
  const order = useMemo(() => cards.map((_, i) => i), [cards]);

  const total = cards.length;
  const card = cards[index];

  const next = () => {
    setRevealed(false);
    if (index < total - 1) {
      setIndex(index + 1);
    } else if (softQueue.length > 0) {
      const nextIdx = softQueue[0];
      setSoftQueue(softQueue.slice(1));
      setIndex(nextIdx);
    } else {
      onComplete?.();
    }
  };

  const comeBackToThis = () => {
    setRevealed(false);
    setSoftQueue((q) => [...q, index]);
    if (index < total - 1) setIndex(index + 1);
    else {
      const [head, ...rest] = softQueue;
      setSoftQueue(rest);
      if (head !== undefined) setIndex(head);
      else onComplete?.();
    }
  };

  if (!card) return null;
  const isLast = index === total - 1 && softQueue.length === 0;

  return (
    <div
      className="relative min-h-screen flex flex-col px-5 pt-12 pb-10 overflow-hidden"
      style={{ background: P.bgWash }}
      data-testid={`card-deck-${variant}`}
    >
      {/* ── Subtle blurred road backdrop on deep navy ─────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${API_BASE}/api/journey-images/uk-driving-theory/day1_awareness.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.10,
          filter: "blur(32px) saturate(0.6) brightness(0.7)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,0.78) 0%, rgba(27,42,58,0.9) 100%)",
        }}
        aria-hidden
      />

      {/* ── Precision-card horizon line ─────────────────────────────
         Only renders for precision variant. A barely-there 1px cool-blue
         gradient drifting slowly behind the card — distinguishes the
         "focus mode" sub-step without crossing back into illustration. */}
      {variant === "precision" && (
        <>
          <motion.div
            className="absolute pointer-events-none"
            initial={{ opacity: 0, x: "-12%" }}
            animate={{ opacity: 0.55, x: "12%" }}
            transition={{
              opacity: { duration: 2.4, ease: "easeOut" },
              x: { duration: 22, ease: "linear", repeat: Infinity, repeatType: "reverse" },
            }}
            style={{
              left: 0,
              right: 0,
              top: "52%",
              height: 1,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(77,163,255,0.55) 35%, rgba(77,163,255,0.55) 65%, transparent 100%)",
            }}
            aria-hidden
          />
          {/* faint glow above the line */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: "10%",
              right: "10%",
              top: "48%",
              height: 28,
              background:
                "radial-gradient(ellipse at 50% 100%, rgba(77,163,255,0.18) 0%, rgba(77,163,255,0) 70%)",
              filter: "blur(6px)",
            }}
            aria-hidden
          />
        </>
      )}

      {/* ── Header — variant + pearl progress ──────────────────────────── */}
      <div className="relative flex items-center justify-between max-w-md w-full mx-auto mb-8">
        <p
          className="text-[10px] uppercase tracking-[0.36em] font-medium"
          style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL }}
        >
          {VARIANT_COPY[variant]?.label || "Card"}
        </p>
        <div className="flex gap-1.5" aria-label={`${index + 1} of ${total}`}>
          {order.map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all duration-500"
              style={{
                width: i === index ? 16 : 6,
                background:
                  i === index
                    ? P.accentBlue
                    : i < index
                    ? P.accentBlue + "55"
                    : "rgba(255,255,255,0.12)",
              }}
            />
          ))}
        </div>
      </div>

      {/* ── Card body ─────────────────────────────────────────────────── */}
      <div className="relative flex-1 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${index}-${revealed}`}
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: revealed ? 1.015 : 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.99 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-md rounded-[26px] overflow-hidden"
            style={{
              // Glass card on navy — semi-transparent + backdrop blur
              background: revealed
                ? "rgba(255,255,255,0.10)"
                : "rgba(255,255,255,0.06)",
              backdropFilter: "blur(22px) saturate(1.2)",
              WebkitBackdropFilter: "blur(22px) saturate(1.2)",
              border: `1px solid ${revealed ? "rgba(77,163,255,0.32)" : "rgba(255,255,255,0.10)"}`,
              boxShadow: revealed
                ? "0 30px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 1px 0 rgba(255,255,255,0.16) inset, 0 0 36px rgba(77,163,255,0.18)"
                : "0 18px 44px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 1px 0 rgba(255,255,255,0.12) inset",
            }}
            data-testid={`card-${index}`}
          >
            {/* Illustration — blurred while in question state, clears on reveal.
                Precision cards are pure micro-drills (stats / facts) and
                deliberately render no illustration — per design system:
                "if decorative, remove it".
                Cards without a real photo (image_url null — e.g. Day 3+
                cards awaiting AI image generation) ALSO render no
                illustration — a clean text-only card looks intentional;
                an empty dark box looks broken. */}
            {variant !== "precision" && card.image_url && (
              <div className="px-3 pt-3">
                <CardIllustration
                  variant={variant}
                  imageUrl={card.image_url}
                  illustrationKey={card.illustration_key}
                  revealed={revealed}
                  text={`${card.front || ""} ${card.back || ""}`}
                />
              </div>
            )}

            {/* Text region */}
            <div className="px-6 pt-6 pb-7">
              <AnimatePresence mode="wait" initial={false}>
                {!revealed ? (
                  <motion.button
                    key="front"
                    onClick={() => setRevealed(true)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="w-full text-left cursor-pointer"
                    data-testid={`card-front-${index}`}
                  >
                    <p
                      className="text-[20px] sm:text-[22px] leading-[1.32]"
                      style={{
                        color: P.textPrimary,
                        fontFamily: JOURNEY_FONT_HEAD,
                        fontWeight: 400,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {card.front}
                    </p>
                    <p
                      className="text-[10.5px] tracking-[0.32em] uppercase mt-6"
                      style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
                    >
                      {VARIANT_COPY[variant]?.hint || "Tap to reveal"}
                    </p>
                  </motion.button>
                ) : (
                  <motion.div
                    key="back"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`card-back-${index}`}
                  >
                    <p
                      className="text-[10px] uppercase tracking-[0.34em] mb-3"
                      style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 600 }}
                    >
                      What it means
                    </p>
                    <p
                      className="text-[15.5px] sm:text-[16px] leading-[1.6]"
                      style={{
                        color: P.textPrimary,
                        fontFamily: JOURNEY_FONT_BODY,
                        fontWeight: 400,
                      }}
                    >
                      {card.back}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Actions ───────────────────────────────────────────────────── */}
      <div className="relative max-w-md w-full mx-auto mt-8 space-y-2.5">
        <motion.button
          onClick={next}
          disabled={!revealed}
          animate={{ opacity: revealed ? 1 : 0.32 }}
          whileTap={{ scale: 0.985 }}
          whileHover={revealed ? { scale: 1.012 } : {}}
          transition={{ type: "spring", stiffness: 280, damping: 24 }}
          className="w-full rounded-full py-[14px] flex items-center justify-center gap-2 transition-all"
          style={{
            background: revealed
              ? `linear-gradient(135deg, ${P.accentBlue} 0%, ${P.accentBlueDeep} 100%)`
              : "rgba(77,163,255,0.10)",
            color: revealed ? "#FFFFFF" : P.accentBlue,
            fontFamily: JOURNEY_FONT_BODY,
            fontWeight: 500,
            fontSize: 14,
            letterSpacing: "0.01em",
            border: `1px solid ${revealed ? "rgba(77,163,255,0.55)" : "rgba(77,163,255,0.20)"}`,
            cursor: revealed ? "pointer" : "default",
            boxShadow: revealed
              ? "0 12px 30px rgba(46,123,214,0.40), 0 0 0 1px rgba(255,255,255,0.10) inset, 0 1px 0 rgba(255,255,255,0.20) inset, 0 0 24px rgba(77,163,255,0.30)"
              : "none",
          }}
          data-testid="card-next-btn"
        >
          {isLast ? "Continue" : "Got it"}
          <ArrowRight size={15} strokeWidth={2.2} />
        </motion.button>

        <motion.button
          onClick={comeBackToThis}
          disabled={!revealed}
          animate={{ opacity: revealed ? 0.6 : 0 }}
          className="w-full text-[12px] py-2"
          style={{
            color: P.textSecondary,
            fontFamily: JOURNEY_FONT_BODY,
            fontWeight: 400,
            cursor: revealed ? "pointer" : "default",
            pointerEvents: revealed ? "auto" : "none",
          }}
          data-testid="card-soft-btn"
        >
          I'll come back to this
        </motion.button>
      </div>
    </div>
  );
}
